import type { FC } from 'react';
import React, { Fragment, useCallback, useState } from 'react';
import { styled } from '@compiled/react';

import { EXTENSION_POINT_BYLINE_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import {
	ExperienceStart,
	ExperienceSuccess,
} from '@confluence/experience-tracker/entry-points/component.ts';
import { expValEquals } from '@confluence/feature-experiments';
import { RequireLegacyWRM } from '@confluence/wrm/entry-points/RequireLegacyWRM.ts';
import type { ForgeUIContentBylineItemExtensionType } from '@confluence/forge-ui/entry-points/types.ts';
import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay.ts';
import { isErrorMarkedAsHandled } from '@confluence/graphql/entry-points/isErrorMarkedAsHandled.ts';
import type { LoadableType } from '@confluence/loadable';
import { LoadableAfterPaint } from '@confluence/loadable';

import { LivePagesByLineAppsPlaceholder } from './LivePagesByLineAppsPlaceholder.tsx';
import { ByLineAppsComponent } from './ByLineAppsComponent.tsx';
import { AuthoritativeByLineApps } from './AuthoritativeByLineApps.tsx';
import type { ByLineAppSnapshot } from './byLineAppSnapshot.ts';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- display: contents preserves the existing inline byline geometry.
const ByLineAppRuntimeHandoff = styled.span({
	display: 'contents',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- The adjacent runtime marker atomically hides the inert SSR fallback without a duplicate visible frame.
	'& > [data-byline-app-runtime] + [data-byline-app-snapshot]': {
		display: 'none',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- display: contents avoids adding a layout box during the runtime handoff.
const ByLineAppRuntime = styled.span({ display: 'contents' });

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- display: contents avoids adding a layout box during the runtime handoff.
const ByLineAppSnapshotFallback = styled.span({ display: 'contents' });

type ByLineAppsProps = {
	contentId: string;
	connectItems: object[];
	forgeItems: ReadonlyArray<ForgeUIContentBylineItemExtensionType>;
	forgeError?: Error;
	isSingleLineByline?: boolean;
	showLivePagesExtensionPoints?: boolean;
	onBylineAppsLoaded?: () => void;
	loadingConnectItems?: boolean;
	loadingForgeItems?: boolean;
	noContainer?: boolean;
	authoritativeSnapshot?: ByLineAppSnapshot;
};

export const LivePagesByLineApps: FC<ByLineAppsProps> = ({
	contentId,
	connectItems,
	forgeItems,
	forgeError,
	isSingleLineByline,
	onBylineAppsLoaded,
	showLivePagesExtensionPoints,
	loadingConnectItems = false,
	loadingForgeItems = false,
	noContainer = false,
	authoritativeSnapshot,
}) => {
	const isLoading = loadingConnectItems || loadingForgeItems;
	const [showLoader, setShowLoader] = useState<boolean>(
		isLoading &&
			!expValEquals('confluence_skip_byline_apps_placeholder', 'isEnabled', true) &&
			!(authoritativeSnapshot && expValEquals('confluence_ssr_byline', 'isEnabled', true)),
	);
	const handleLoad = useCallback(() => {
		setShowLoader(false);
		onBylineAppsLoaded?.();
	}, [onBylineAppsLoaded]);
	const shouldRenderBylineApps = expValEquals(
		'confluence_skip_byline_apps_placeholder',
		'isEnabled',
		true,
	)
		? !isLoading
		: true;
	const runtimeApps = (
		<RequireLegacyWRM
			key="byline-app-wrm"
			wrm="wr!com.atlassian.plugins.atlassian-connect-plugin:confluence-atlassian-connect-content-byline-resources-v5"
		>
			{() => {
				if (
					forgeError &&
					(!authoritativeSnapshot || !expValEquals('confluence_ssr_byline', 'isEnabled', true))
				) {
					if (!isErrorMarkedAsHandled(forgeError)) {
						return <ErrorDisplay error={forgeError} />;
					}
					// Process handled errors as successful
					return <ExperienceSuccess name={EXTENSION_POINT_BYLINE_EXPERIENCE} />;
				}

				const unhandledForgeError =
					forgeError && !isErrorMarkedAsHandled(forgeError) ? forgeError : undefined;
				const apps = (
					<Fragment>
						{unhandledForgeError ? <ErrorDisplay error={unhandledForgeError} /> : null}
						<ByLineAppsComponent
							// this key is required in order to ensure that a byline app remounts when transitioning to a different page
							// remounting is required to ensure there is no stale data being presented when an app is opened on a second page
							key={contentId}
							contentId={contentId}
							forgeItems={forgeError ? [] : forgeItems}
							connectItems={connectItems}
							showLivePagesExtensionPoints={showLivePagesExtensionPoints}
							onLoad={handleLoad}
							showLoader={showLoader}
							isSingleLineByline={isSingleLineByline}
							hasAuthoritativeBylineAppSnapshot={Boolean(authoritativeSnapshot)}
						/>
						{unhandledForgeError ? null : (
							<ExperienceSuccess name={EXTENSION_POINT_BYLINE_EXPERIENCE} />
						)}
					</Fragment>
				);

				return authoritativeSnapshot && expValEquals('confluence_ssr_byline', 'isEnabled', true) ? (
					<ByLineAppRuntime data-byline-app-runtime>{apps}</ByLineAppRuntime>
				) : (
					apps
				);
			}}
		</RequireLegacyWRM>
	);

	return (
		<Fragment>
			<ExperienceStart name={EXTENSION_POINT_BYLINE_EXPERIENCE} />
			{showLoader && (
				<LivePagesByLineAppsPlaceholder
					connectItems={connectItems}
					forgeItems={forgeItems}
					noContainer={noContainer}
				/>
			)}
			{shouldRenderBylineApps &&
				(authoritativeSnapshot && expValEquals('confluence_ssr_byline', 'isEnabled', true) ? (
					<ByLineAppRuntimeHandoff>
						{runtimeApps}
						<ByLineAppSnapshotFallback data-byline-app-snapshot>
							<AuthoritativeByLineApps snapshot={authoritativeSnapshot} />
						</ByLineAppSnapshotFallback>
					</ByLineAppRuntimeHandoff>
				) : (
					runtimeApps
				))}
		</Fragment>
	);
};

export const LazyLivePagesByLineApps: LoadableType<ByLineAppsProps> = LoadableAfterPaint({
	loader: async () => {
		return (
			await import(
				/* webpackChunkName: "loadable-LivePagesByLineApps" */ './LivePagesByLineApps.tsx'
			)
		).LivePagesByLineApps;
	},
	loading: (props) =>
		props.authoritativeSnapshot && expValEquals('confluence_ssr_byline', 'isEnabled', true) ? (
			<AuthoritativeByLineApps snapshot={props.authoritativeSnapshot} />
		) : expValEquals('confluence_skip_byline_apps_placeholder', 'isEnabled', true) ? null : (
			<LivePagesByLineAppsPlaceholder {...props} />
		),
});
